import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  FormControlLabel,
  Switch,
  Alert,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import { userApi, User } from '../services/api';

interface UserDialogProps {
  open: boolean;
  user: User | null;
  onClose: (refresh: boolean) => void;
}

export const UserDialog: React.FC<UserDialogProps> = ({ open, user, onClose }) => {
  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    role: '',
    organization_name: '',
    zip_code: '',
    city: '',
    state: '',
    phone: '',
    personal_email: '',
    region: '',
    security_clearance: '',
    field_ready: false,
    is_active: true,
    lms_enabled: true,
    rms_enabled: false,
    rms_unlocked: false
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const roles = ['inspector', 'mac_npe_reviewers', 'admin', 'program_admin'];

  useEffect(() => {
    if (user) {
      setFormData({
        email: user.email || '',
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        role: user.role || '',
        organization_name: user.organization_name || '',
        zip_code: user.zip_code || '',
        city: user.city || '',
        state: user.state || '',
        phone: user.phone || '',
        personal_email: user.personal_email || '',
        region: user.region || '',
        security_clearance: user.security_clearance || '',
        field_ready: user.field_ready || false,
        is_active: user.is_active,
        lms_enabled: user.lms_enabled,
        rms_enabled: user.rms_enabled,
        rms_unlocked: user.rms_unlocked
      });
    } else {
      setFormData({
        email: '',
        first_name: '',
        last_name: '',
        role: '',
        organization_name: '',
        zip_code: '',
        city: '',
        state: '',
        phone: '',
        personal_email: '',
        region: '',
        security_clearance: '',
        field_ready: false,
        is_active: true,
        lms_enabled: true,
        rms_enabled: false,
        rms_unlocked: false
      });
    }
    setError('');
  }, [user, open]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked } = e.target;
    const switchFields = ['is_active', 'field_ready', 'lms_enabled', 'rms_enabled', 'rms_unlocked'];
    setFormData(prev => ({
      ...prev,
      [name]: switchFields.includes(name) ? checked : value
    }));
  };

  const handleSelectChange = (e: any) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (user) {
        await userApi.updateUser(user.id, formData);
      } else {
        await userApi.createUser(formData);
      }
      onClose(true);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save user');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="md" fullWidth>
      <DialogTitle>{user ? 'Edit User' : 'Add New User'}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              required
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              disabled={!!user}
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                required
                label="First Name"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
              />

              <TextField
                fullWidth
                required
                label="Last Name"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  name="role"
                  value={formData.role}
                  onChange={handleSelectChange}
                  label="Role"
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {roles.map((role) => (
                    <MenuItem key={role} value={role}>
                      {role.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Organization"
                name="organization_name"
                value={formData.organization_name}
                onChange={handleChange}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Zip Code"
                name="zip_code"
                value={formData.zip_code}
                onChange={handleChange}
              />

              <TextField
                fullWidth
                label="City"
                name="city"
                value={formData.city}
                onChange={handleChange}
              />

              <TextField
                fullWidth
                label="State"
                name="state"
                value={formData.state}
                onChange={handleChange}
              />
            </Box>

            <TextField
              fullWidth
              label="Phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
            />

            <TextField
              fullWidth
              label="Personal Email"
              name="personal_email"
              type="email"
              value={formData.personal_email}
              onChange={handleChange}
              helperText="Alternative email address for the user"
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Region"
                name="region"
                value={formData.region}
                onChange={handleChange}
                helperText="Geographic region or state"
              />

              <TextField
                fullWidth
                label="Security Clearance"
                name="security_clearance"
                value={formData.security_clearance}
                onChange={handleChange}
                helperText="Security clearance level"
              />
            </Box>

            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Access Controls</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.is_active}
                        onChange={handleChange}
                        name="is_active"
                        color="success"
                      />
                    }
                    label="Active"
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.field_ready}
                        onChange={handleChange}
                        name="field_ready"
                        color="info"
                      />
                    }
                    label="Field Ready"
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.lms_enabled}
                        onChange={handleChange}
                        name="lms_enabled"
                        color="primary"
                      />
                    }
                    label="LMS Enabled"
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.rms_enabled}
                        onChange={handleChange}
                        name="rms_enabled"
                        color="secondary"
                      />
                    }
                    label="RMS Enabled"
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.rms_unlocked}
                        onChange={handleChange}
                        name="rms_unlocked"
                        color="warning"
                        disabled={!formData.rms_enabled}
                      />
                    }
                    label="RMS Unlocked"
                  />
                </Grid>
              </Grid>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => onClose(false)}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={loading}>
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

